/**
 * PersonaFade — wraps page content with a 250ms fade-in keyed on the active persona.
 * When the persona switcher changes activeId, React remounts this subtree, triggering
 * the CSS animation. Honors prefers-reduced-motion per WCAG 2.1 criterion 2.3.3.
 *
 * Also gates visual render on persona hydration — prevents 50-200ms P1-default flicker
 * while Zustand rehydrates from localStorage.
 * Atlas: global shell.
 */
"use client";

import { usePersonaStore, usePersonaHasHydrated } from "@/lib/persona";

function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <h1 className="sr-only">Loading dashboard</h1>
      <div className="h-8 w-48 rounded bg-neutral-200" aria-hidden />
      <div className="h-48 rounded-xl bg-neutral-200" aria-hidden />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" aria-hidden>
        <div className="h-32 rounded-xl bg-neutral-200" />
        <div className="h-32 rounded-xl bg-neutral-200" />
        <div className="h-32 rounded-xl bg-neutral-200" />
      </div>
    </div>
  );
}

export function PersonaFade({ children }: { children: React.ReactNode }) {
  const activeId = usePersonaStore((s) => s.activeId);
  const hasHydrated = usePersonaHasHydrated();

  if (!hasHydrated) return <DashboardSkeleton />;

  return (
    <div key={activeId} className="animate-fade-in motion-reduce:animate-none">
      {children}
    </div>
  );
}
